/**
 * Widget Polling Hook
 *
 * Custom hook for automatic widget data refresh
 */

import { useEffect, useRef } from 'react';

export interface UseWidgetPollingOptions {
  /** Polling interval in milliseconds */
  interval: number;
  /** Refresh callback */
  onRefresh: () => void;
  /** Whether polling is enabled */
  enabled?: boolean;
}

/**
 * Hook for automatic widget data refresh with configurable interval
 */
export function useWidgetPolling({
  interval,
  onRefresh,
  enabled = true,
}: UseWidgetPollingOptions): void {
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const callbackRef = useRef(onRefresh);

  // Keep callback ref up to date
  useEffect(() => {
    callbackRef.current = onRefresh;
  }, [onRefresh]);

  useEffect(() => {
    if (!enabled || interval <= 0) {
      return;
    }

    // Set up polling interval
    intervalRef.current = setInterval(() => {
      callbackRef.current();
    }, interval);

    // Cleanup on unmount or when interval changes
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [interval, enabled]);
}
